import type { CSSProperties, JSX } from 'react';
import { CloseIcon } from '../../../_components/Icons.js';
import { IconButton } from '../../../_components/IconButton.js';
import type { Palette } from '../../../_theme/palette.js';

interface NudgeTeaserProps {
  palette: Palette;
  headline?: string;
  onAccept: () => void;
  onClose: () => void;
  onSuppress: () => void;
}

const DEFAULT_HEADLINE = 'Got a minute for a quick survey?';

export function NudgeTeaser({
  palette,
  headline,
  onAccept,
  onClose,
  onSuppress,
}: NudgeTeaserProps): JSX.Element {
  return (
    <div role="dialog" aria-label="Survey invitation" style={containerStyle(palette)}>
      <AccentStripe palette={palette} />
      <div style={bodyStyle()}>
        <div style={topRowStyle()}>
          <p style={headlineStyle(palette)}>{headline?.trim() || DEFAULT_HEADLINE}</p>
          <IconButton onClick={onClose} ariaLabel="Close" color={palette.textMuted} size={28}>
            <CloseIcon size={16} />
          </IconButton>
        </div>
        <button type="button" onClick={onAccept} style={primaryButtonStyle(palette)}>
          Open survey
        </button>
        <button type="button" onClick={onSuppress} style={suppressLinkStyle(palette)}>
          Don't show again
        </button>
      </div>
    </div>
  );
}

function AccentStripe({ palette }: { palette: Palette }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 4,
        background: `linear-gradient(90deg, ${palette.primary}, ${palette.primaryDeep})`,
      }}
    />
  );
}

function containerStyle(palette: Palette): CSSProperties {
  return {
    position: 'fixed',
    right: 20,
    bottom: 20,
    width: 'min(340px, calc(100vw - 40px))',
    background: palette.card,
    color: palette.text,
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 24px 48px rgba(15, 23, 42, 0.28), 0 2px 6px rgba(15, 23, 42, 0.08)',
    fontFamily: palette.font,
    border: `1px solid ${palette.border}`,
    // One-shot bounce-in to gently draw attention without being intrusive.
    animation: 'chirp-nudge-in 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
  };
}

function bodyStyle(): CSSProperties {
  return { padding: '16px 16px 14px', display: 'flex', flexDirection: 'column', gap: 12 };
}

function topRowStyle(): CSSProperties {
  return { display: 'flex', alignItems: 'flex-start', gap: 10 };
}

function headlineStyle(palette: Palette): CSSProperties {
  return {
    flex: 1,
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
    color: palette.text,
  };
}

function primaryButtonStyle(palette: Palette): CSSProperties {
  return {
    width: '100%',
    padding: '10px 16px',
    borderRadius: 10,
    border: 'none',
    background: palette.primary,
    color: palette.onPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

function suppressLinkStyle(palette: Palette): CSSProperties {
  return {
    background: 'transparent',
    border: 'none',
    padding: '2px 4px',
    color: palette.textMuted,
    fontSize: 12,
    cursor: 'pointer',
    alignSelf: 'center',
    textDecoration: 'underline',
  };
}
